import React, { useState, useEffect } from 'react'
import { Box } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

// Components
import GeneralSection from '../../../components/billing/editBill/GeneralSection'
import EntrySection from '../../../components/billing/editBill/EntrySection'
import NetTotalSection from '../../../components/billing/editBill/NetTotalSection'
import EditBillHeader from '../../../components/billing/editBill/EditBillHeader'

// Others
import { calculateNetTotal, formatUpdateBillMutationData, processEntries } from '../../../utils/billing'
import {
  useFetchBillDetailsLazyQuery,
  FetchBillDetailsQuery,
  EntryType,
  useUpdateBillMutation
} from '../../../../generated'
import { GRAPHQL_QUERY_POLICY } from '../../../constants'
import { Alert } from '../../../components/common/Alert'
import { EditBillForm } from '../../../interfaceTypes/forms'
import { editBillEntryValidationSchema } from '../../../validationSchema'

const EditBill: React.FC = () => {
  const [bill, setBill] = useState<FetchBillDetailsQuery['fetchBillDetails']['bill']>()
  const { id: billId } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const methods = useForm<EditBillForm>({
    resolver: yupResolver(editBillEntryValidationSchema),
    mode: 'onChange',
    defaultValues: {
      timeEntries: [],
      legalFeeEntries: [],
      expenseEntries: [],
      investigatorEntries: [],
      discountPercentage: null
    }
  })

  const { handleSubmit, reset, watch } = methods
  const { netTotal, discount } = calculateNetTotal(watch())

  const [updateBillMutation, { loading: updatingBill }] = useUpdateBillMutation({
    onError(error) {
      Alert.error(error.message || 'Failed to update bill')
    },
    onCompleted(data) {
      if (data) {
        const {
          updateBill: { response }
        } = data

        if (response) {
          const { status } = response

          if (status === 200) {
            Alert.success('Bill updated successfully!')
            navigate(`/billing/bill-detail/${billId}`)
          } else {
            Alert.error('Failed to update bill')
          }
        }
      }
    }
  })

  const [fetchBillDetails] = useFetchBillDetailsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError(error) {
      Alert.error(error.message || 'Failed to fetch bill details')
    },
    onCompleted(data) {
      if (data?.fetchBillDetails) {
        const { bill, response } = data.fetchBillDetails
        const { status } = response || {}

        if (status === 200 && bill) {
          const entries = bill.billItems?.map(item => item.entry) || []
          const { timeEntries, legalFeeEntries, expenseEntries, investigatorEntries } = processEntries(entries)

          setBill(bill)
          reset({
            timeEntries,
            legalFeeEntries,
            expenseEntries,
            investigatorEntries,
            discountPercentage: bill.discountPercentage
          })
        }
      }
    }
  })

  const onSubmit: SubmitHandler<EditBillForm> = async data => {
    if (!bill) return

    const formattedData = formatUpdateBillMutationData({
      bill: bill,
      data,
      totalAmount: netTotal,
      discount
    })

    await updateBillMutation({
      variables: {
        updateBillRequestInput: formattedData
      }
    })
  }

  useEffect(() => {
    if (billId) {
      fetchBillDetails({
        variables: {
          fetchBillDetailsInput: {
            billId: parseInt(billId)
          }
        }
      })
    }
  }, [billId, fetchBillDetails])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box position="relative">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <EditBillHeader bill={bill} netTotal={netTotal} updatingBill={updatingBill} />

            <GeneralSection bill={bill} />

            <EntrySection
              key="timeEntries"
              title="Time Entries"
              fieldName="timeEntries"
              entryType={EntryType.Time}
              caseId={bill?.case?.id || ''}
              caseUsers={bill?.case?.caseToUsers || []}
              billingType={bill?.case?.billing?.billingType}
              isFlatFeeHoursExhausted={bill?.case?.billing?.isFlatFeeHoursExhausted}
            />

            <EntrySection
              key="legalFeeEntries"
              title="Legal fee"
              fieldName="legalFeeEntries"
              entryType={EntryType.Legal}
              caseId={bill?.case?.id || ''}
              caseUsers={bill?.case?.caseToUsers || []}
              billingType={bill?.case?.billing?.billingType}
              isFlatFeeHoursExhausted={bill?.case?.billing?.isFlatFeeHoursExhausted}
            />

            <EntrySection
              key="expenseEntries"
              title="Expense Entries"
              fieldName="expenseEntries"
              entryType={EntryType.Expense}
              caseId={bill?.case?.id || ''}
              caseUsers={bill?.case?.caseToUsers || []}
              billingType={bill?.case?.billing?.billingType}
              isFlatFeeHoursExhausted={bill?.case?.billing?.isFlatFeeHoursExhausted}
            />

            <EntrySection
              key="investigatorEntries"
              title="Investigator Cost Entries"
              fieldName="investigatorEntries"
              entryType={EntryType.Cost}
              caseId={bill?.case?.id || ''}
              caseUsers={bill?.case?.caseToUsers || []}
              billingType={bill?.case?.billing?.billingType}
              isFlatFeeHoursExhausted={bill?.case?.billing?.isFlatFeeHoursExhausted}
            />

            <NetTotalSection netTotal={netTotal} bill={bill} />
          </form>
        </FormProvider>
      </Box>
    </LocalizationProvider>
  )
}

export default EditBill
